import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Loader2, Send, HelpCircle, CheckCircle } from 'lucide-react';
import { Label } from '../components/ui/label';
import api from '../services/api';

const SupportPage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  
  const [formData, setFormData] = useState({
    subject: '',
    message: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);

    if (!formData.subject.trim() || !formData.message.trim()) {
      setError('Please fill in all fields');
      return;
    }

    setLoading(true);

    try {
      await api.post('/support/submit', {
        email: user.email,
        subject: formData.subject.trim(),
        message: formData.message.trim()
      });
      setSuccess(true);
      setFormData({ subject: '', message: '' });
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-2xl" data-testid="support-page">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-airbus-blue/10 rounded-sm flex items-center justify-center">
            <HelpCircle className="w-5 h-5 text-airbus-blue" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold text-cockpit-text-primary">Support</h1>
            <p className="text-sm text-cockpit-text-secondary">Get help from the Flight Sim Academy team</p>
          </div>
        </div>

        {/* Success State */}
        {success ? (
          <div className="card-panel p-8 text-center" data-testid="support-success">
            <div className="w-16 h-16 bg-status-success/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-status-success" />
            </div>
            <h2 className="text-lg font-medium text-cockpit-text-primary mb-2">Message Sent</h2>
            <p className="text-cockpit-text-secondary mb-6">Your message has been sent. We will get back to you shortly.</p>
            <button
              onClick={() => setSuccess(false)}
              className="btn-secondary"
              data-testid="support-send-another"
            >
              Send Another Message
            </button>
          </div>
        ) : (
          /* Form */
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Error Message */}
            {error && (
              <div className="p-3 bg-red-900/30 border border-red-900 rounded-sm">
                <p className="text-sm text-red-300" data-testid="support-error">{error}</p>
              </div>
            )}

            <div className="card-panel p-6">
              <div className="space-y-4">
                {/* Email (Read-only) */}
                <div>
                  <Label className="label-text block mb-2">Your Email</Label>
                  <input
                    type="email"
                    value={user?.email || ''}
                    readOnly
                    className="input-field w-full bg-cockpit-bg/50 cursor-not-allowed opacity-70"
                    data-testid="support-email"
                  />
                  <p className="text-xs text-cockpit-text-secondary/50 mt-1">
                    We'll respond to this email address
                  </p>
                </div>

                {/* Subject */}
                <div>
                  <Label className="label-text block mb-2">Subject</Label>
                  <input
                    type="text"
                    value={formData.subject}
                    onChange={(e) => setFormData(prev => ({ ...prev, subject: e.target.value }))}
                    className="input-field w-full"
                    placeholder="Brief description of your issue"
                    maxLength={100}
                    data-testid="support-subject"
                  />
                </div>

                {/* Message */}
                <div>
                  <Label className="label-text block mb-2">Message</Label>
                  <textarea
                    value={formData.message}
                    onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                    className="input-field w-full min-h-[150px] resize-y"
                    placeholder="Describe your issue in detail..."
                    maxLength={2000}
                    data-testid="support-message"
                  />
                  <p className="text-xs text-cockpit-text-secondary/50 mt-1">
                    {formData.message.length}/2000 characters
                  </p>
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="btn-primary flex items-center gap-2"
              data-testid="support-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  Send Message
                </>
              )}
            </button>
          </form>
        )}
      </div>
    </DashboardLayout>
  );
};

export default SupportPage;
